<template>
  <div :class="joinClasses('dropdown', modifiers)">
    <button
      class="button button--transparent"
      type="button"
      @click="toggleFilter"
    >
      <span class="typo-tiny">{{ $t('general.filter_by_date') }}</span>
      <UseSvg
        class="icon button__icon button__icon--left-space"
        name="filter"
      />
    </button>
    <Popper
      v-model:open="open"
      :modifiers="['cover']"
      placement="bottom-end"
    >
      <div class="dropdown__content date-filter">
        <FilterMobileHeader
          :label="$t('general.filter_by_date')"
          @toggle="toggleFilter"
        />

        <div class="dropdown__body">
          <div class="dropdown__group dropdown__group--mobile-stack">
            <div
              v-for="radio in radioList"
              :key="`${radio.label}_${radio.id}`"
              class="dropdown__input-field"
            >
              <label :for="`${radio.label}_${radio.id}`">
                <input
                  :id="`${radio.label}_${radio.id}`"
                  v-model="radioDate"
                  :class="radio?.label === radioDate?.label && 'active'"
                  name="radioDate"
                  type="radio"
                  :value="radio"
                  @change="clearCalendar()"
                >
                <span>{{ $t(`general.${radio.label}`) }}</span>
              </label>
            </div>
          </div>

          <template v-if="showCustomDateRange">
            <p class="dropdown__label">
              {{ $t('general.select_range') }}
            </p>
            <div class="dropdown__group dropdown__group--gap dropdown__group--wrap">
              <div class="form-layout__content">
                <FormRow
                  :label="$t('general.from_date')"
                  labelFor="fromDate"
                  :modifiers="['no-padding']"
                >
                  <div class="form-row__input form-row__input--no-peek">
                    <div class="input">
                      <input
                        id="fromDate"
                        v-model="fromDateInput"
                        type="date"
                      >
                    </div>
                  </div>
                  <FormDatePicker
                    id="fromDatePicker"
                    v-model="fromDate"
                    class="dropdown__datePicker show-for-medium"
                    hideIconAndLabel
                  />
                </FormRow>
              </div>
              <div class="form-layout__content">
                <FormRow
                  :errors="errors"
                  :label="$t('general.to_date')"
                  labelFor="toDate"
                  :modifiers="['no-padding']"
                >
                  <div class="form-row__input form-row__input--no-peek">
                    <div :class="{ input: true, 'input--error': !!errors }">
                      <input
                        id="toDate"
                        v-model="toDateInput"
                        type="date"
                      >
                    </div>
                  </div>
                  <FormDatePicker
                    id="toDatePicker"
                    v-model="toDate"
                    class="dropdown__datePicker show-for-medium"
                    hideIconAndLabel
                  />
                </FormRow>
              </div>
            </div>
          </template>

          <div class="dropdown__group dropdown__group--actions">
            <button
              class="button button--danger"
              type="button"
              @click="clearAll"
            >
              <span>{{ $t('general.clear_all_filters') }}</span>
            </button>

            <div class="dropdown__group">
              <button
                class="button show-for-medium"
                type="button"
                @click="toggleFilter"
              >
                <span>{{ $t('general.cancel') }}</span>
              </button>

              <button
                class="button button--important button--important button--auto"
                type="button"
                @click="apply"
              >
                <span>{{ $t('general.apply') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Popper>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import I18n from 'i18n-js';
import { format, subYears } from 'date-fns';
import { v4 as uuid } from 'uuid';
import joinClasses from '@/functions/join_classes';

// Components
import FormRow from '@/components/form/row.vue';
import FormDatePicker from '@/components/form/date_picker.vue';
import FilterMobileHeader from '@/components/generic/date_filter_mobile_header.vue';
import Popper from '@/components/generic/popper.vue';

// Svg
import UseSvg from '@/components/generic/use_svg.vue';

const props = defineProps({
  /** Unique id for the component */
  id: { type: String, default: uuid },
  /** Earliest graph date value */
  earliestGraphDate: { type: String, required: false, default: null },
  /** Model value */
  modelValue: { type: Object, default: null },
  /** Shows or hides custom date rage */
  showCustomDateRange: { type: Boolean, required: false, default: true },
  /** Additional classes you may add */
  modifiers: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const errors = ref(null);
const radioDate = ref(null);
const fromDate = ref(null);
const toDate = ref(null);
const fromDateInput = ref(null);
const toDateInput = ref(null);
const open = ref(false);
const today = new Date();
const yearStart = new Date(`${today.getFullYear()}-01-01`);
const pastThree = subYears(new Date(), 3);

const radioList = [
  {
    id: props.id,
    label: 'year_to_date',
    value() {
      return {
        from: format(yearStart, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      };
    },
  },
  {
    id: props.id,
    label: 'last_three_years',
    value() {
      return {
        from: format(pastThree, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      };
    },
  },
  {
    id: props.id,
    label: 'all_time',
    value() {
      return {
        from: props.earliestGraphDate,
        to: format(today, 'yyyy-MM-dd'),
      };
    },
  },
];

/**
 * Loads the component with dates on the calendar or radio selection
 */
onMounted(() => {
  loadDate();
});

/**
 * The bottom two watch the date-picker component
 */
watch(fromDate, (value) => {
  fromDateInput.value = value && format(new Date(value?.toString()), 'yyyy-MM-dd');
});

watch(toDate, (value) => {
  toDateInput.value = value && format(new Date(value?.toString()), 'yyyy-MM-dd');
});

/**
 * The bottom two watch the date input field
 * mostly useful on mobile where the date-picker is not being used
 */
watch(fromDateInput, (value) => {
  value && validateDate();
});

watch(toDateInput, (value) => {
  value && validateDate();
});

/**
 * Toggles the filter menu (popper)
 */
function toggleFilter() {
  open.value = !open.value;
}

/**
 * Clears the calendar values and errors
 * and radio button value
 */
function clearAll() {
  radioDate.value = null;
  clearCalendar();
  emit('update:modelValue', { id: props.id });
}

/**
 * Clears the calendar values and errors
 */
function clearCalendar() {
  fromDate.value = null;
  toDate.value = null;
  fromDateInput.value = null;
  toDateInput.value = null;
  errors.value = null;
}

/**
 * Loads the radio or calendar depending on the
 * value the modelValue is set to
 */
function loadDate() {
  if (props.modelValue?.label === 'date_picker') {
    fromDate.value = new Date(props.modelValue?.from);
    toDate.value = new Date(props.modelValue?.to);
  } else {
    const selectRadio = radioList.filter(item => item.label === props.modelValue?.label);
    radioDate.value = selectRadio[0];
  }
}

/**
 * Validates the date input fields and
 * sets the error if from-date is later than to-date
 */
function validateDate() {
  radioDate.value = null;

  if (!fromDateInput.value || !toDateInput.value) return;

  const newFromDate = new Date(fromDateInput.value).getTime();
  const newToDate = new Date(toDateInput.value).getTime();

  if (newFromDate <= newToDate) {
    errors.value = null;
  } else {
    errors.value = [I18n.t('activerecord.errors.date_filter.toAndFrom')];
  }
}

/**
 * Checks for errors and values then emits the value
 * and closes the filter
 */
function apply() {
  if (
    errors.value ||
    !(radioDate.value || (fromDateInput.value && toDateInput.value))
  ) return;

  let value;
  if (radioDate.value) {
    value = {
      ...radioDate.value?.value(),
      id: props.id,
      label: radioDate.value.label,
    };
  } else {
    value = {
      id: props.id,
      label: 'date_picker',
      from: fromDateInput.value,
      to: toDateInput.value,
    };
  }
  emit('update:modelValue', value);
  toggleFilter();
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

@mixin content-size {
  min-width: bu(54);
  height: auto;
}

.form-layout__content {
  position: relative;
}

.dropdown {
  display: block;

  &--no-padding-right {
    .button {
      &--transparent {
        padding-right: 0;
      }
    }
  }

  .button {
    font-weight: normal;
    color: map-get($foreground-color, default);
  }

  &__datePicker {
    position: absolute;
    top: bu(4);
    width: 100%;
    height: bu(5);
    height: auto;
  }

  &__input-field {
    input {
      display: none;

      & + span {
        display: inline-block;
        position: relative;
        padding-left: bu(2.5);

        @include breakpoint(small) {
          padding-bottom: bu(2.5);
        }

        @include breakpoint(medium) {
          padding-bottom: 0;
        }

        &:before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: rem(-8);
          border-radius: 50%;
          width: bu(2.5);
          height: bu(2.5);
          border: 1px solid map-get($foreground-color, light);
        }

        &:after {
          content: '';
          display: block;
          width: bu(1.8);
          height: bu(1.8);
          background: map-get($foreground-color, light);
          position: absolute;
          border-radius: 50%;
          top: rem(3);
          opacity: 0;
          transform: scale(0, 0);
          transition: all 0.2s cubic-bezier(0.64, 0.57, 0.67, 1.53);

          @include breakpoint(small) {
            left: rem(-5);
          }

          @include breakpoint(medium) {
            left: rem(-5.4);
          }
        }
      }

      &.active + span:after {
        opacity: 1;
        transform: scale(1, 1);
      }
    }
  }

  &__label {
    @include breakpoint(small) {
      padding: bu(2.5) bu(1.4);
    }

    @include breakpoint(medium) {
      padding: bu(2.5) 0;
    }
  }

  &__content {
    z-index: 1;

    @include breakpoint(small) {
      height: 100%;
      width: 100%;
    }

    @include breakpoint(medium) {
      @include content-size();
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: map-get($background-color, light);
    z-index: 1;
    box-shadow: map-get($box-shadow, popover);

    @include breakpoint(small) {
      width: 100%;
      padding: bu(2) 0;
      border-radius: 0;
    }

    @include breakpoint(medium) {
      @include content-size();
      padding: bu(4);
      border-radius: bu(1.4);
    }
  }

  &__group {
    display: flex;
    justify-content: space-between;

    @include breakpoint(small) {
      &--mobile-stack {
        flex-direction: column;
      }
      padding: 0 bu(1.4);
    }

    @include breakpoint(medium) {
      &--mobile-stack {
        flex-direction: row;
      }
      padding: 0;
    }

    &--gap {
      gap: bu(3);
    }

    &--wrap {
      flex-wrap: wrap;
    }

    &--actions {
      border-top: 1px solid map-get($background-color, lightest);
      .button {
        color: $white;
        font-weight: 700;
      }

      @include breakpoint(small) {
        margin-top: bu(2.5);
        padding-top: bu(2.5);
      }
    }
  }

  input[type='date']::-webkit-inner-spin-button,
  input[type='date']::-webkit-calendar-picker-indicator {
    @include breakpoint(medium) {
      display: none;
      -webkit-appearance: none;
    }
  }
}
</style>
